import * as React from "react";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { cn } from "../lib/utils";

export interface ComboboxOption {
  value:   string;
  label:   string;
  /** Optional sub-label shown below the main label (e.g. supporting text). */
  hint?:   string;
  /** Optional third line under `hint` - used for tertiary info like
   *  opening hours on a parcel-machine option. */
  subline?: string;
  /** Optional emoji / unicode glyph rendered before the label (flags, etc). */
  prefix?: React.ReactNode;
  disabled?: boolean;
}

interface ComboboxProps {
  options:      ComboboxOption[];
  value?:       string;
  onChange:     (v: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?:  string;
  disabled?:    boolean;
  clearable?:   boolean;
  /** Accessible name of the clear (x) control. Default English "clear". */
  clearLabel?:  string;
  className?:   string;
  /** Width of the popover panel - defaults to the trigger's width. */
  popoverClassName?: string;
  /**
   * Set `true` when the Combobox lives inside a modal Dialog. A non-modal
   * Popover renders in a portal outside the Dialog subtree, so the Dialog's
   * `react-remove-scroll` lock blocks wheel/touch scrolling inside the option
   * list. A modal Popover manages its own scroll, so the list scrolls again.
   * Defaults to `false` (page-level use), where modal would needlessly lock the
   * page scroll and shift layout when the panel opens.
   */
  modal?:       boolean;
  /**
   * Lets the user keep what they typed when no option carries that label - a maker a
   * catalogue does not list, say. Offered as the last item while the search text matches
   * no label. Absent = the list is closed, which is every other use of this component.
   */
  onCreate?:    (text: string) => void;
  /** The words of that item for the typed text. Defaults to the text itself. */
  createLabel?: (text: string) => string;
  /** The trigger's id, so a visible `<label htmlFor>` names it. */
  id?:          string;
  /** The trigger's accessible name when no visible label points at it. */
  "aria-label"?:      string;
  "aria-labelledby"?: string;
}

/**
 * cmdk's default filter ranks substring matches purely by character overlap,
 * so typing "poppins" against a 1500-item list scores "Poltawski" / "Doppio"
 * higher than the exact match. Custom scoring rules below:
 *   1.0  - exact match
 *   0.9  - starts-with on any token (so "Poppins" beats "Doppio" for "po")
 *   0.5  - substring match
 *   0.0  - no match (hidden)
 * `value` arrives space-joined (label + value + hint + subline).
 */
function comboboxFilter(value: string, search: string): number {
  if (!search) return 1;
  const v = value.toLowerCase();
  const q = search.toLowerCase();
  if (v === q) return 1;
  // Tokenise on whitespace; favour any token that starts with the query.
  for (const tok of v.split(/\s+/)) {
    if (tok === q) return 1;
    if (tok.startsWith(q)) return 0.9;
  }
  if (v.startsWith(q)) return 0.85;
  if (v.includes(q))   return 0.5;
  return 0;
}

/**
 * Combobox - div-based trigger + Radix Popover + cmdk Command. Replaces
 * native <select> across the platform (chapter 0d.4). Supports search,
 * keyboard navigation, optional clearable, hint sub-labels, and prefix
 * icons/emoji. Same pattern used in ajaraam.
 */
export function Combobox({
  options, value, onChange, placeholder, searchPlaceholder,
  emptyLabel = "No results.", disabled, clearable = false, clearLabel = "clear", className, popoverClassName,
  modal = false, onCreate, createLabel, id, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  // Held here rather than inside cmdk so the "keep what I typed" item can read it. Cleared
  // on close, which is what the uncontrolled input did by unmounting with the popover.
  const [search, setSearch] = React.useState("");
  const selected = options.find((o) => o.value === value);
  const typed = search.trim();
  const canCreate =
    !!onCreate && typed !== "" && !options.some((o) => o.label.toLowerCase() === typed.toLowerCase());

  function toggle(next: boolean) {
    setOpen(next);
    if (!next) setSearch("");
  }

  return (
    <Popover open={open} onOpenChange={toggle} modal={modal}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          // Named like every other primitive in the kit so a host app can retune
          // the trigger once in CSS instead of hand-passing utilities at each
          // call site (the CRM restyles it into its own pill this way).
          data-slot="combobox-trigger"
          id={id}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-10 w-full inline-flex items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-sm shadow-soft",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
            "disabled:cursor-not-allowed disabled:opacity-50 hover:bg-accent/30 transition-colors",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <span className="inline-flex items-center gap-2 truncate">
            {selected?.prefix}
            <span className="truncate">{selected?.label ?? placeholder ?? ""}</span>
          </span>
          <span className="inline-flex items-center gap-1 shrink-0">
            {clearable && selected && (
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                className="size-5 rounded-sm hover:bg-destructive/15 text-muted-foreground hover:text-destructive inline-flex items-center justify-center"
                aria-label={clearLabel}
              >
                <X className="size-3" />
              </span>
            )}
            <ChevronDown className="size-4 text-muted-foreground" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn("p-0 w-[var(--radix-popover-trigger-width)] min-w-[12rem]", popoverClassName)}
      >
        <Command filter={comboboxFilter}>
          <CommandInput value={search} onValueChange={setSearch} placeholder={searchPlaceholder ?? placeholder ?? ""} />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  // Composite key: some lists (EMTAK activities) reuse the same
                  // `value` across synonym labels; label+value stays unique.
                  key={`${o.value}\u0000${o.label}`}
                  value={`${o.label} ${o.value} ${o.hint ?? ""} ${o.subline ?? ""}`}
                  disabled={o.disabled}
                  onSelect={() => { onChange(o.value); toggle(false); }}
                >
                  {o.prefix}
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{o.label}</div>
                    {o.hint && <div className="text-xs text-muted-foreground truncate">{o.hint}</div>}
                    {o.subline && <div className="text-xs text-muted-foreground/80 truncate">{o.subline}</div>}
                  </div>
                  <Check className={cn("size-4 text-primary", value === o.value ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ))}
              {canCreate && (
                <CommandItem
                  key="__create__"
                  // Always passes the filter: the value begins with exactly what was typed.
                  value={`${typed} __create__`}
                  onSelect={() => { onCreate!(typed); toggle(false); }}
                >
                  <Plus className="size-4 text-muted-foreground" />
                  <span className="flex-1 min-w-0 truncate">{createLabel ? createLabel(typed) : typed}</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
